// src/views/usuarios/UsuariosList.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import PlanLimitBadge from "../../components/ui/PlanLimitBadge";
import condominiosService  from "../../services/condominiosService";
import permissoesService  from "../../services/permissoesService";
import { getApiBaseUrl } from "../../services/apiBase";
import ConfirmModal from "../../components/ui/ConfirmModal";

const BASE = getApiBaseUrl();
// CPF/CNPJ helpers
function limparDoc(v) { return (v||"").replace(/\D/g,""); }
function formatarDoc(v) {
  const d = limparDoc(v);
  if (d.length <= 11) { // CPF
    return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/,"$1.$2.$3-$4")
            .replace(/(\d{3})(\d{3})(\d{1,3})/,"$1.$2.$3")
            .replace(/(\d{3})(\d{1,3})/,"$1.$2")
            .substring(0,14);
  }
  return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,"$1.$2.$3/$4-$5")
          .replace(/(\d{2})(\d{3})(\d{3})(\d{1,4})/,"$1.$2.$3/$4")
          .replace(/(\d{2})(\d{3})(\d{1,3})/,"$1.$2.$3")
          .replace(/(\d{2})(\d{1,3})/,"$1.$2")
          .substring(0,18);
}
function validarCPF(cpf) {
  const d = limparDoc(cpf);
  if (d.length !== 11 || /^(\d)+$/.test(d)) return false;
  let s = 0;
  for(let i=0;i<9;i++) s += parseInt(d[i])*(10-i);
  let r = (s*10)%11; if(r===10||r===11) r=0;
  if(r!==parseInt(d[9])) return false;
  s=0;
  for(let i=0;i<10;i++) s += parseInt(d[i])*(11-i);
  r=(s*10)%11; if(r===10||r===11) r=0;
  return r===parseInt(d[10]);
}
function validarCNPJ(cnpj) {
  const d = limparDoc(cnpj);
  if (d.length !== 14) return false;
  const calc = (d, n) => {
    let s=0, p=n-7;
    for(let i=0;i<n;i++){ s += parseInt(d[i])*p; p = p===2?9:p-1; }
    const r=s%11; return r<2?0:11-r;
  };
  return calc(d,12)===parseInt(d[12]) && calc(d,13)===parseInt(d[13]);
}
function validarDoc(v) {
  const d = limparDoc(v);
  if (d.length===11) return validarCPF(v);
  if (d.length===14) return validarCNPJ(v);
  return false;
}

// Lista completa (usada para exibir o perfil na lista). Mantém "Condomino"
// para compatibilidade com usuários legados que possam ter sido criados pela tela.
const PERFIS = { Admin:"Admin", Gerente:"Gerente", Sindico:"Síndico", Condomino:"Condômino", Porteiro:"Porteiro", Portaria:"Portaria", Operacional:"Operacional" };
// Apenas perfis OPERACIONAIS — usados no dropdown do modal.
// "Condomino" foi REMOVIDO daqui: agora é criado exclusivamente pela ficha do
// Condômino → Acesso ao Portal (mantém invariantes idCondomino/cpf_cnpj).
const PERFIS_MODAL = { Admin:"Admin", Gerente:"Gerente", Sindico:"Síndico", Porteiro:"Porteiro", Portaria:"Portaria", Operacional:"Operacional" };
const PERFIL_COLOR = {
  Admin:    {bg:"#FEE2E2",color:"#991B1B"},
  Gerente:  {bg:"#FFF7ED",color:"#C2410C"},
  Sindico:  {bg:"#EFF6FF",color:"#1D4ED8"},
  Condomino:{bg:"#F0FDF4",color:"#166534"},
  Porteiro: {bg:"#F1F5F9",color:"var(--gray-600)"},
  Portaria: {bg:"#F1F5F9",color:"var(--gray-600)"},
  Operacional: {bg:"#ECFDF5",color:"#047857"},
};
const fmtD = d => { try{ return new Date((String(d||"").split("T")[0])+"T12:00:00").toLocaleDateString("pt-BR"); }catch{ return "—"; } };

const EMPTY = { nome:"", email:"", senha:"", perfil:"Sindico", ativo:1, telefone:"", condominios:[], idCondomino:"", cpf_cnpj:"" };

export default function UsuariosList() {
  const { authFetch, usuario: usuarioLogado } = useAuth();
  const [data, setData]           = useState([]);
  const [clientes, setClientes]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [pageError, setPageError] = useState(null);
  const [pagination, setPag]      = useState({total:0,totalPages:1});
  const [filtros, setFiltros]     = useState({perfil:"",ativo:"",search:"",page:1});

  const [condominios, setCondominios] = useState([]);
  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData]   = useState(null);
  const [form, setForm]           = useState(EMPTY);
  const [errors, setErrors]       = useState({});
  const [saving, setSaving]       = useState(false);
  const [serverError, setServerError] = useState(null);
  const [mostrarSenha,   setMostrarSenha]   = useState(false);
  const [condominoSearch,setCondominoSearch] = useState("");
  const [condominoList,  setCondominoList]   = useState([]);
  const [searchingCond,  setSearchingCond]   = useState(false);
  // Permissões modal
  const [showPerm, setShowPerm]       = useState(false);
  const [permTarget, setPermTarget]   = useState(null);
  const [permissoes, setPermissoes]   = useState({});
  const [modulos, setModulos]         = useState([]);
  const [savingPerm, setSavingPerm]   = useState(false);
  const [permError, setPermError]     = useState(null);
  const [permSuccess, setPermSuccess] = useState(false);

  // Senha modal
  const [showSenha, setShowSenha] = useState(false);
  const [targetSenha, setTargetSenha] = useState(null);
  const [novaSenha, setNovaSenha] = useState("");
  const [savingSenha, setSavingSenha] = useState(false);
  const [senhaError, setSenhaError]   = useState(null);
  const [senhaSuccess, setSenhaSuccess] = useState(false);

  // Confirmação global
  const [confirmacao, setConfirmacao] = useState(null);
  const askConfirm = (opts) => new Promise(resolve => setConfirmacao({ ...opts, resolve }));

  useEffect(() => {
    condominiosService.listar({limit:200}).then(r=>setCondominios(r.data||[])).catch(()=>{});
  },[]);



  const carregar = useCallback(async () => {
    setLoading(true); setPageError(null);
    try {
      const qs = new URLSearchParams(Object.entries(filtros).filter(([,v])=>v!==""&&v!==undefined));
      const r = await authFetch("GET", `/usuarios?${qs}&limit=20`);
      const parsed = (r.data||[]).map(u=>({
        ...u,
        condominioIds: u.condominioIdsStr
          ? u.condominioIdsStr.split(",").map(Number)
          : [],
      }));
      setData(parsed); setPag(r.pagination||{total:0,totalPages:1});
    } catch(e){ setPageError(e.message || "Erro ao carregar usuários."); }
    setLoading(false);
  },[filtros, authFetch]);

  useEffect(()=>{ carregar(); },[carregar]);

  useEffect(()=>{
    if (!usuarioLogado?.isSuperAdmin) return;
    authFetch("GET","/saas/clientes?limit=100").then(r=>setClientes(r.data||[])).catch(()=>{});
  },[usuarioLogado, authFetch]);

  const setF = (f,v) => setFiltros(p=>({...p,[f]:v,page:1}));
  const setFm = (f,v) => { setForm(p=>({...p,[f]:v})); setErrors(p=>({...p,[f]:undefined})); setServerError(null); };
  const buscarCondomino = async (q) => {
    setCondominoSearch(q);
    if (q.trim().length < 1) { setCondominoList([]); return; }
    setSearchingCond(true);
    try {
      const r = await authFetch("GET", `/condominos/search?q=${encodeURIComponent(q)}`);
      setCondominoList(r.data||[]);
    } catch(e) { setCondominoList([]); }
    setSearchingCond(false);
  };

  const selecionarCondomino = (cond) => {
    setFm("idCondomino", cond.id);
    setFm("cpf_cnpj", cond.cpf_cnpj||"");
    setCondominoSearch(cond.nmCompleto);
    setCondominoList([]);
  };

  const abrirModal = (item=null) => {
    setEditData(item);
    setForm(item ? {...EMPTY,...item, senha:"", condominios: item.condominioIds||[]} : {...EMPTY});
    setCondominoSearch(item?.nmCondominoVinculado||"");
    setCondominoList([]);
    setErrors({}); setServerError(null); setMostrarSenha(false); setShowModal(true);
  };

  const validate = () => {
    const e={};
    if(!form.nome.trim())  e.nome="Nome obrigatório";
    if(!form.email.trim()) e.email="E-mail obrigatório";
    else if(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email="E-mail inválido";
    const usaSenhaPadrao = ["Condomino", "Operacional"].includes(form.perfil);
    if(!editData && !usaSenhaPadrao && !form.senha.trim()) e.senha="Senha obrigatória";
    if(!editData && !usaSenhaPadrao && form.senha && form.senha.length<6) e.senha="Mínimo 6 caracteres";
    if(form.perfil==="Condomino") {
      if(!form.idCondomino) e.idCondomino="Selecione o condômino vinculado";
    }
    if (["Porteiro", "Portaria", "Operacional"].includes(form.perfil)) {
      const qtd = form.condominios?.length || 0;
      if (qtd !== 1) e.condominios = "Este perfil exige exatamente um condomínio vinculado.";
    }
    // Valida CPF/CNPJ se preenchido
    if (form.cpf_cnpj && limparDoc(form.cpf_cnpj).length > 0) {
      const len = limparDoc(form.cpf_cnpj).length;
      if (len !== 11 && len !== 14) {
        e.cpf_cnpj = "Documento incompleto (CPF=11 dígitos, CNPJ=14)";
      } else if (!validarDoc(form.cpf_cnpj)) {
        e.cpf_cnpj = len === 11 ? "CPF inválido" : "CNPJ inválido";
      }
    }
    setErrors(e); return Object.keys(e).length===0;
  };

  const handleSave = async () => {
    if(!validate()) return;
    setSaving(true); setServerError(null);
    try {
      const payload = { ...form, condominios: form.condominios||[] };
      if(!payload.senha) delete payload.senha;
      if(editData?.id) await authFetch("PUT",`/usuarios/${editData.id}`,payload);
      else             await authFetch("POST","/usuarios",payload);
      setShowModal(false); await carregar();
    } catch(e){ setServerError(e.message || "Erro ao salvar usuário."); }
    setSaving(false);
  };

  const handleAlterarSenha = async () => {
    setSenhaError(null); setSenhaSuccess(false);
    if(!novaSenha || novaSenha.length<6) {
      setSenhaError("Senha deve ter ao menos 6 caracteres.");
      return;
    }
    setSavingSenha(true);
    try {
      await authFetch("PATCH",`/usuarios/${targetSenha.id}/senha`,{senha:novaSenha});
      setSenhaSuccess(true); setNovaSenha("");
      setTimeout(() => { setShowSenha(false); setSenhaSuccess(false); }, 1500);
    } catch(e){ setSenhaError(e.message || "Erro ao alterar senha."); }
    setSavingSenha(false);
  };

  const handleToggleAtivo = async (u) => {
    setPageError(null);
    try {
      await authFetch("PUT",`/usuarios/${u.id}`,{ativo: u.ativo?0:1});
      await carregar();
    } catch(e){ setPageError(e.message); }
  };

  const handleDelete = async (u) => {
    const ok = await askConfirm({
      titulo:"Remover usuário",
      mensagem:`Confirma a remoção do usuário "${u.nome}"? Esta ação não pode ser desfeita.`,
      textoOk:"Remover", perigo:true,
    });
    if (!ok) return;
    setPageError(null);
    try { await authFetch("DELETE",`/usuarios/${u.id}`); await carregar(); }
    catch(e){ setPageError(e.message); }
  };

  const handleAbrirPermissoes = async (u) => {
    setPermTarget(u); setPermError(null); setPermSuccess(false);
    try {
      const r = await permissoesService.buscar(u.id);
      setPermissoes(r.data.permissoes||{});
      setModulos(r.data.modulos||[]);
    } catch(e) { setPermError(e.message || "Erro ao carregar permissões."); }
    setShowPerm(true);
  };

  const handleSalvarPermissoes = async () => {
    setSavingPerm(true); setPermError(null); setPermSuccess(false);
    try {
      await permissoesService.salvar(permTarget.id, permissoes);
      // Se for o próprio usuário logado, recarrega permissões da sessão (C1)
      if (permTarget.id === usuarioLogado?.id) {
        try {
          const me = await permissoesService.minhas();
          if (me?.data?.permissoes) {
            localStorage.setItem("permissoes", JSON.stringify(me.data.permissoes));
            // Recarregar a página para o sidebar refletir as novas permissões
            setPermSuccess(true);
            setTimeout(() => window.location.reload(), 1200);
            setSavingPerm(false);
            return;
          }
        } catch { /* silencioso */ }
      }
      setPermSuccess(true);
      setTimeout(() => { setShowPerm(false); setPermSuccess(false); }, 1500);
    } catch(e) { setPermError(e.message || "Erro ao salvar permissões."); }
    setSavingPerm(false);
  };

  const toggleCondo = (id) => setFm("condominios",
    form.condominios.includes(id)
      ? form.condominios.filter(x=>x!==id)
      : [...form.condominios, id]
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title"><i className="bi bi-people me-2" style={{color:"var(--primary)",fontSize:22}}/>Usuários</h1>
          <p className="page-subtitle">Gerencie perfis de acesso e vínculos de condomínio</p>
        </div>
        <button className="btn btn-primary" onClick={()=>abrirModal()}>
          <i className="bi bi-plus-lg"/> Novo Usuário
        </button>
      </div>

      {pageError && (
        <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert"
          style={{marginBottom:12}}>
          <span><i className="bi bi-exclamation-triangle-fill me-2"/>{pageError}</span>
          <button className="btn-close" onClick={()=>setPageError(null)} aria-label="Close"/>
        </div>
      )}

      <div className="card">
        {/* Filtros */}
        <div style={{padding:"10px 16px",borderBottom:"1px solid var(--gray-100)",display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          <input className="form-control form-control-sm" style={{width:220}} placeholder="Buscar nome ou e-mail..."
            value={filtros.search} onChange={e=>setF("search",e.target.value)}/>
          {usuarioLogado?.isSuperAdmin && (
            <select className="form-select form-select-sm" style={{width:"auto",maxWidth:180}}
              value={filtros.filterCliente} onChange={e=>setF("filterCliente",e.target.value)}>
              <option value="">Todos os clientes</option>
              <option value="0">Sem cliente (sistema)</option>
              {clientes.map(cl=>(
                <option key={cl.id} value={cl.id}>{cl.nmCliente}</option>
              ))}
            </select>
          )}
          <select className="form-select form-select-sm" style={{width:"auto"}} value={filtros.perfil} onChange={e=>setF("perfil",e.target.value)}>
            <option value="">Todos os perfis</option>
            {Object.entries(PERFIS).map(([v,l])=><option key={v} value={v}>{l}</option>)}
          </select>
          <select className="form-select form-select-sm" style={{width:"auto"}} value={filtros.ativo} onChange={e=>setF("ativo",e.target.value)}>
            <option value="">Todos</option><option value="1">Ativos</option><option value="0">Inativos</option>
          </select>
          <button className="btn btn-outline-secondary btn-sm" onClick={carregar}><i className="bi bi-arrow-clockwise"/></button>
          <span style={{marginLeft:"auto",fontSize:12.5,color:"var(--gray-400)"}}>{pagination.total} usuário(s)</span>
        </div>

        <div className="table-responsive">
          <table className="table table-custom">
            <thead><tr><th>Usuário</th><th>Perfil</th><th>Condomínios</th><th>Último Acesso</th><th>Status</th><th style={{width:110}}>Ações</th></tr></thead>
            <tbody>
              {loading?<tr><td colSpan={6} className="text-center py-4"><span className="spinner-border spinner-border-sm text-primary"/></td></tr>
              :data.length===0?<tr><td colSpan={6} className="text-center py-4" style={{color:"var(--gray-400)"}}>Nenhum usuário encontrado</td></tr>
              :data.map(u=>{
                const ps = PERFIL_COLOR[u.perfil]||PERFIL_COLOR.Sindico;
                return (
                  <tr key={u.id} style={{opacity:u.ativo?1:.5}}>
                    <td>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <div style={{width:36,height:36,borderRadius:"50%",background:"linear-gradient(135deg,var(--primary),var(--accent))",
                          display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:700,fontSize:13,flexShrink:0}}>
                          {u.nome.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{fontWeight:600,fontSize:13.5}}>{u.nome}</div>
                          <div style={{fontSize:12,color:"var(--gray-400)"}}>{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span style={{...ps,padding:"3px 10px",borderRadius:12,fontSize:12,fontWeight:700}}>
                        {PERFIS[u.perfil]||u.perfil}
                      </span>
                      {u.idCondomino && (
                        <span title="Acesso criado pela ficha do Condômino"
                          style={{marginLeft:6,fontSize:10,padding:"2px 6px",borderRadius:10,
                            background:"#F0F9FF",color:"#0369A1",fontWeight:600}}>
                          <i className="bi bi-house-heart me-1"/>Condômino
                        </span>
                      )}
                    </td>
                    <td>
                      {u.perfil==="Admin"
                        ? <span style={{fontSize:12,color:"var(--gray-400)"}}>Todos</span>
                        : <span style={{fontSize:12.5,fontWeight:600}}>{u.qtdCondominios||0}</span>
                      }
                    </td>
                    <td style={{fontSize:12.5,color:"var(--gray-500)"}}>{u.dtUltimoAcesso?fmtD(u.dtUltimoAcesso):"Nunca"}</td>
                    <td>
                      <div className="form-check form-switch mb-0">
                        <input className="form-check-input" type="checkbox" checked={!!u.ativo}
                          onChange={()=>handleToggleAtivo(u)}
                          disabled={u.id===usuarioLogado?.id && u.perfil==="Admin"}/>
                      </div>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button className="btn btn-outline-secondary btn-sm btn-icon" title="Editar"
                          onClick={()=>abrirModal(u)}><i className="bi bi-pencil"/></button>
                        {usuarioLogado?.perfil==="Admin" && u.perfil!=="Admin" && (
                          <button className="btn btn-outline-primary btn-sm btn-icon" title="Permissões"
                            onClick={()=>handleAbrirPermissoes(u)}>
                            <i className="bi bi-shield-check"/>
                          </button>
                        )}
                        {u.idCondomino ? (
                          <span className="btn btn-outline-secondary btn-sm btn-icon" title="Senha/sessões gerenciadas na ficha do Condômino"
                            style={{cursor:"help",opacity:.6}}><i className="bi bi-house-heart"/></span>
                        ) : (
                          <button className="btn btn-outline-secondary btn-sm btn-icon" title="Alterar senha"
                            onClick={()=>{setTargetSenha(u);setNovaSenha("");setShowSenha(true);}}><i className="bi bi-key"/></button>
                        )}
                        {u.id!==usuarioLogado?.id && !u.idCondomino && (
                          <button className="btn btn-outline-danger btn-sm btn-icon"
                            onClick={()=>handleDelete(u)}><i className="bi bi-trash3"/></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pagination.totalPages>1&&(
          <div className="pagination-wrapper">
            <nav><ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${filtros.page===1?"disabled":""}`}><button className="page-link" onClick={()=>setF("page",filtros.page-1)}><i className="bi bi-chevron-left"/></button></li>
              {Array.from({length:pagination.totalPages},(_,i)=><li key={i+1} className={`page-item ${filtros.page===i+1?"active":""}`}><button className="page-link" onClick={()=>setF("page",i+1)}>{i+1}</button></li>)}
              <li className={`page-item ${filtros.page===pagination.totalPages?"disabled":""}`}><button className="page-link" onClick={()=>setF("page",filtros.page+1)}><i className="bi bi-chevron-right"/></button></li>
            </ul></nav>
          </div>
        )}
      </div>

      {/* ── MODAL USUÁRIO ── */}
      {showModal&&(
        <div className="modal fade show d-block" style={{background:"rgba(0,0,0,.55)"}}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editData?"Editar Usuário":"Novo Usuário"}</h5>
                <button className="btn-close btn-close-white" onClick={()=>setShowModal(false)}/>
              </div>
              <div className="modal-body">
                {serverError && (
                  <div className="alert alert-danger d-flex align-items-center justify-content-between"
                    role="alert" style={{marginBottom:12}}>
                    <span><i className="bi bi-exclamation-triangle-fill me-2"/>{serverError}</span>
                    <button className="btn-close" onClick={()=>setServerError(null)}/>
                  </div>
                )}
                <div className="form-section">
                  <div className="form-section-title"><i className="bi bi-person"/> Dados do Usuário</div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Nome <span className="required-mark">*</span></label>
                      <input className={`form-control ${errors.nome?"is-invalid":""}`}
                        value={form.nome} onChange={e=>setFm("nome",e.target.value)} autoComplete="name"/>
                      {errors.nome&&<div className="invalid-feedback">{errors.nome}</div>}
                    </div>
                    {/* Quando o usuário é um Condômino (vinculado), os campos pessoais
                        são sincronizados pelo cadastro do Condômino (read-only aqui). */}
                    {(() => {
                      const ehCondominoVinc = editData && editData.idCondomino && form.perfil === "Condomino";
                      return (
                        <>
                    <div className="col-md-6">
                      <label className="form-label">E-mail <span className="required-mark">*</span></label>
                      <input type="email" className={`form-control ${errors.email?"is-invalid":""}`}
                        value={form.email} onChange={e=>setFm("email",e.target.value)}
                        readOnly={ehCondominoVinc}
                        style={ehCondominoVinc?{background:"var(--bg-surface-alt)"}:undefined}/>
                      {errors.email&&<div className="invalid-feedback">{errors.email}</div>}
                      {ehCondominoVinc && (
                        <div className="form-text" style={{fontSize:11}}>
                          <i className="bi bi-lock me-1"/>Sincronizado pela ficha do Condômino.
                        </div>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Perfil</label>
                      {ehCondominoVinc ? (
                        <input className="form-control" value="Condômino" readOnly
                          style={{background:"var(--bg-surface-alt)"}}/>
                      ) : (
                        <select className="form-select" value={form.perfil} onChange={e=>{
                                const p = e.target.value;
                                setFm("perfil", p);
                                setFm("senha", "");
                              }}>
                          {Object.entries(PERFIS_MODAL).map(([v,l])=><option key={v} value={v}>{l}</option>)}
                        </select>
                      )}
                      {!editData && (
                        <div className="form-text" style={{fontSize:11}}>
                          <i className="bi bi-info-circle me-1"/>
                          Para acesso de <strong>Condômino</strong>, use a ficha do Condômino → aba "Acesso ao Portal".
                        </div>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Telefone</label>
                      <input className="form-control" value={form.telefone||""} onChange={e=>setFm("telefone",e.target.value)}
                        autoComplete="off" type="tel"
                        readOnly={ehCondominoVinc}
                        style={ehCondominoVinc?{background:"var(--bg-surface-alt)"}:undefined}/>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Status</label>
                      <select className="form-select" value={form.ativo} onChange={e=>setFm("ativo",parseInt(e.target.value))}>
                        <option value={1}>Ativo</option><option value={0}>Inativo</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">CPF / CNPJ</label>
                      <input className={`form-control ${errors.cpf_cnpj?"is-invalid":""}`}
                        value={formatarDoc(form.cpf_cnpj||"")}
                        onChange={e=>setFm("cpf_cnpj", limparDoc(e.target.value))}
                        placeholder="000.000.000-00 ou 00.000.000/0000-00"
                        autoComplete="off"
                        readOnly={ehCondominoVinc}
                        style={ehCondominoVinc?{background:"var(--bg-surface-alt)"}:undefined}/>
                      {errors.cpf_cnpj && <div className="invalid-feedback">{errors.cpf_cnpj}</div>}
                    </div>
                        </>
                      );
                    })()}

                    {/* Seletor de Condômino vinculado */}
                    {form.perfil === "Condomino" && (
                      <div className="col-12">
                        <label className="form-label">
                          <i className="bi bi-person-badge me-1"/>
                          Condômino vinculado <span className="required-mark">*</span>
                        </label>
                        <div style={{position:"relative"}}>
                          <input className={`form-control ${errors.idCondomino?"is-invalid":""}`}
                            value={condominoSearch}
                            onChange={e=>buscarCondomino(e.target.value)}
                            placeholder="Digite o nome ou CPF/CNPJ para buscar..."
                            autoComplete="off"/>
                          {searchingCond && (
                            <span className="spinner-border spinner-border-sm"
                              style={{position:"absolute",right:10,top:10,color:"var(--primary)"}}/>
                          )}
                          {condominoList.length > 0 && (
                            <div style={{position:"absolute",top:"100%",left:0,right:0,
                              background:"var(--bg-surface)",border:"1px solid var(--gray-200)",
                              borderRadius:8,zIndex:100,boxShadow:"0 4px 16px rgba(0,0,0,.1)",
                              maxHeight:200,overflowY:"auto"}}>
                              {condominoList.map(cd=>(
                                <div key={cd.id}
                                  onClick={()=>selecionarCondomino(cd)}
                                  style={{padding:"10px 14px",cursor:"pointer",
                                    borderBottom:"1px solid var(--gray-100)",fontSize:13}}
                                  onMouseEnter={e=>e.currentTarget.style.background="#F8FAFC"}
                                  onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                                  <div style={{fontWeight:600}}>{cd.nmCompleto}</div>
                                  <div style={{fontSize:11,color:"var(--gray-400)"}}>
                                    {cd.cpf_cnpj||"Sem CPF/CNPJ"} · {cd.tpPessoa==="Fisica"?"Pessoa Física":"Pessoa Jurídica"}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {errors.idCondomino && <div className="invalid-feedback d-block">{errors.idCondomino}</div>}
                        {form.idCondomino && (
                          <div style={{marginTop:6,fontSize:12,color:"var(--success,#166534)",
                            display:"flex",alignItems:"center",gap:6}}>
                            <i className="bi bi-check-circle-fill"/>
                            Condômino vinculado — CPF/CNPJ: {form.cpf_cnpj||"não informado"}
                          </div>
                        )}
                        <small style={{fontSize:11,color:"var(--gray-400)"}}>
                          O condômino deve estar previamente cadastrado em Cadastros → Condôminos
                        </small>
                      </div>
                    )}
                    {!editData&&(
                      <div className="col-md-6">
                        {["Condomino", "Operacional"].includes(form.perfil) ? (
                          <div>
                            <label className="form-label">Senha</label>
                            <div style={{background:"var(--bg-success-soft)",border:"1px solid #86EFAC",
                              borderRadius:8,padding:"10px 14px",fontSize:12.5,color:"#166534"}}>
                              <i className="bi bi-shield-check me-2"/>
                              Senha temporária <strong>Condo@2026</strong> será enviada por email.
                              O usuário deverá trocá-la no primeiro acesso.
                            </div>
                          </div>
                        ) : (
                          <div>
                            <label className="form-label">Senha <span className="required-mark">*</span></label>
                            <div className="input-group">
                              <input type={mostrarSenha?"text":"password"}
                                className={`form-control ${errors.senha?"is-invalid":""}`}
                                value={form.senha} onChange={e=>setFm("senha",e.target.value)}
                                placeholder="Mínimo 6 caracteres"/>
                              <button className="btn btn-outline-secondary" type="button" onClick={()=>setMostrarSenha(p=>!p)}>
                                <i className={`bi bi-eye${mostrarSenha?"-slash":""}`}/>
                              </button>
                              {errors.senha&&<div className="invalid-feedback">{errors.senha}</div>}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Condomínios vinculados (não para Admin nem Condômino) */}
                {form.perfil!=="Admin" && form.perfil!=="Condomino" && form.perfil!=="AdminSaaS" && form.perfil!=="SuperAdmin" &&(
                  <div className="form-section">
                    <div className="form-section-title d-flex justify-content-between">
                      <span><i className="bi bi-buildings"/> Condomínios Vinculados</span>
                      <span style={{fontSize:12,color:"var(--gray-400)"}}>{form.condominios?.length||0} selecionado(s)</span>
                    </div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:6,maxHeight:160,overflowY:"auto"}}>
                      {condominios.map(c=>{
                        const sel = form.condominios?.includes(c.id);
                        return (
                          <div key={c.id} onClick={()=>toggleCondo(c.id)}
                            style={{padding:"5px 12px",borderRadius:16,fontSize:12.5,cursor:"pointer",border:"2px solid",
                              borderColor:sel?"var(--primary)":"var(--gray-200)",
                              background:sel?"var(--primary)":"#fff",color:sel?"#fff":"var(--gray-600)"}}>
                            {c.nmFantasia||c.nmCondominio}
                          </div>
                        );
                      })}
                    </div>
                    {form.perfil==="Sindico"&&<small style={{color:"var(--gray-400)",fontSize:11}}>Síndico deve ter exatamente um condomínio vinculado.</small>}
                    {["Porteiro","Portaria"].includes(form.perfil)&&<small style={{color:"var(--gray-400)",fontSize:11}}>Usuário de portaria deve ter exatamente um condomínio vinculado.</small>}
                    {form.perfil==="Operacional"&&<small style={{color:"var(--gray-400)",fontSize:11}}>Usuário operacional deve ter exatamente um condomínio vinculado.</small>}
                    {errors.condominios && <div className="text-danger small mt-1">{errors.condominios}</div>}
                  </div>
                )}
                {form.perfil==="Admin"&&(
                  <div style={{background:"#FFF7ED",borderRadius:8,padding:"10px 14px",fontSize:13,color:"#C2410C"}}>
                    <i className="bi bi-shield-fill-exclamation me-2"/>
                    <strong>Admin</strong> tem acesso irrestrito a todos os condomínios do sistema.
                  </div>
                )}
                {form.perfil==="Condomino"&&(
                  <div style={{background:"var(--bg-highlight)",borderRadius:8,padding:"10px 14px",fontSize:13,color:"#1D4ED8"}}>
                    <i className="bi bi-info-circle me-2"/>
                    O acesso do <strong>Condômino</strong> é controlado automaticamente pelo vínculo com o condômino selecionado acima — sem necessidade de selecionar condomínios manualmente.
                  </div>
                )}
                {["Porteiro","Portaria"].includes(form.perfil)&&(
                  <div style={{background:"#FFF7ED",borderRadius:8,padding:"10px 14px",fontSize:13,color:"#C2410C"}}>
                    <i className="bi bi-shield-check me-2"/>
                    Este usuário terá acesso exclusivo ao <strong>Portal da Portaria</strong> — entradas, saídas, encomendas e moradores.
                  </div>
                )}
                {form.perfil==="Operacional"&&(
                  <div style={{background:"#ECFDF5",borderRadius:8,padding:"10px 14px",fontSize:13,color:"#047857"}}>
                    <i className="bi bi-list-check me-2"/>
                    Este usuário terá acesso exclusivo ao <strong>Portal Checklist</strong> (PWA em campo).
                    O plano do cliente precisa incluir os módulos <strong>Checklists</strong> e <strong>Portal Checklist</strong>.
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={()=>setShowModal(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving?<><span className="spinner-border spinner-border-sm me-2"/>Salvando...</>
                  :<><i className="bi bi-check-circle"/> {editData?"Salvar":"Criar Usuário"}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL ALTERAR SENHA ── */}
      {showSenha&&(
        <div className="modal fade show d-block" style={{background:"rgba(0,0,0,.55)"}}>
          <div className="modal-dialog modal-sm">
            <div className="modal-content">
              <div className="modal-header"><h5 className="modal-title">Alterar Senha — {targetSenha?.nome}</h5>
                <button className="btn-close btn-close-white" onClick={()=>{setShowSenha(false);setSenhaError(null);setSenhaSuccess(false);}}/>
              </div>
              <div className="modal-body">
                {senhaError && (
                  <div className="alert alert-danger d-flex align-items-center justify-content-between mb-3" role="alert">
                    <span><i className="bi bi-exclamation-triangle-fill me-2"/>{senhaError}</span>
                    <button className="btn-close" onClick={()=>setSenhaError(null)}/>
                  </div>
                )}
                {senhaSuccess && (
                  <div className="alert alert-success mb-3" role="alert">
                    <i className="bi bi-check-circle-fill me-2"/>Senha alterada com sucesso!
                  </div>
                )}
                <label className="form-label">Nova Senha (mín. 6 caracteres)</label>
                <input type="password" className="form-control" value={novaSenha}
                  onChange={e=>{setNovaSenha(e.target.value);setSenhaError(null);}} placeholder="••••••••"/>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={()=>{setShowSenha(false);setSenhaError(null);setSenhaSuccess(false);}}>Cancelar</button>
                <button className="btn btn-primary" onClick={handleAlterarSenha} disabled={savingSenha||senhaSuccess}>
                  {savingSenha?<span className="spinner-border spinner-border-sm"/>:"Salvar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal de confirmação global ── */}
      {confirmacao && (
        <ConfirmModal
          titulo={confirmacao.titulo}
          mensagem={confirmacao.mensagem}
          textoOk={confirmacao.textoOk}
          perigo={confirmacao.perigo}
          onConfirm={()=>{ confirmacao.resolve(true); setConfirmacao(null); }}
          onCancel={()=>{ confirmacao.resolve(false); setConfirmacao(null); }}
        />
      )}

      {/* ── Modal Permissões ── */}
      {showPerm && permTarget && (
        <div className="modal-overlay" onClick={()=>setShowPerm(false)}>
          <div className="modal-box" style={{maxWidth:620}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-shield-check me-2" style={{color:"var(--primary)"}}/>
                Permissões — {permTarget.nome}
                <span style={{marginLeft:8,fontSize:12,color:"var(--gray-400)",fontWeight:400}}>
                  {permTarget.perfil}
                </span>
              </h5>
              <button className="btn-close" onClick={()=>setShowPerm(false)}/>
            </div>
            <div className="modal-body" style={{maxHeight:500,overflowY:"auto"}}>
              {permError && (
                <div className="alert alert-danger d-flex align-items-center justify-content-between mb-3" role="alert">
                  <span><i className="bi bi-exclamation-triangle-fill me-2"/>{permError}</span>
                  <button className="btn-close" onClick={()=>setPermError(null)}/>
                </div>
              )}
              {permSuccess && (
                <div className="alert alert-success mb-3" role="alert">
                  <i className="bi bi-check-circle-fill me-2"/>
                  Permissões salvas com sucesso!
                  {permTarget.id === usuarioLogado?.id && " A página será recarregada para aplicar."}
                </div>
              )}
              {permTarget.perfil==="Admin" ? (
                <div style={{textAlign:"center",padding:"24px 0",color:"#166534"}}>
                  <i className="bi bi-shield-fill-check" style={{fontSize:40,display:"block",marginBottom:8}}/>
                  <strong>Administrador tem acesso total</strong>
                  <p style={{fontSize:13,color:"var(--gray-500)"}}>Não é possível restringir permissões do Admin.</p>
                </div>
              ) : (
                <>
                  <p style={{fontSize:12.5,color:"var(--gray-500)",marginBottom:14}}>
                    Configure o nível de acesso para cada módulo. <strong>Oculto</strong> = não aparece no menu.
                  </p>
                  {/* Agrupar por seção */}
                  {[...new Set(modulos.map(m=>m.secao))].map(secao=>(
                    <div key={secao} style={{marginBottom:16}}>
                      <div style={{fontSize:11,fontWeight:700,color:"var(--gray-400)",
                        textTransform:"uppercase",letterSpacing:".5px",marginBottom:8,
                        paddingBottom:4,borderBottom:"1px solid var(--gray-100)"}}>
                        {secao}
                      </div>
                      <div style={{display:"flex",flexDirection:"column",gap:6}}>
                        {modulos.filter(m=>m.secao===secao).map(m=>(
                          <div key={m.id} style={{display:"flex",alignItems:"center",gap:10,
                            padding:"8px 12px",borderRadius:8,background:"var(--gray-50)"}}>
                            <i className={`bi ${m.icon}`} style={{color:"var(--primary)",fontSize:16,width:20}}/>
                            <span style={{flex:1,fontSize:13,fontWeight:500,color:"var(--gray-700)"}}>
                              {m.label}
                            </span>
                            {/* Toggle buttons */}
                            <div style={{display:"flex",gap:4}}>
                              {["Total","Visualizar","Oculto"].map(nivel=>(
                                <button key={nivel} onClick={()=>setPermissoes(p=>({...p,[m.id]:nivel}))}
                                  style={{padding:"3px 10px",borderRadius:6,border:"none",
                                    fontSize:11,fontWeight:600,cursor:"pointer",
                                    background:permissoes[m.id]===nivel
                                      ?(nivel==="Total"?"#1B3A6B":nivel==="Visualizar"?"#0F766E":"#64748B")
                                      :"var(--gray-100)",
                                    color:permissoes[m.id]===nivel?"#fff":"var(--gray-500)",
                                  }}>
                                  {nivel}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={()=>setShowPerm(false)}>Fechar</button>
              {permTarget.perfil!=="Admin" && (
                <button className="btn btn-primary" onClick={handleSalvarPermissoes} disabled={savingPerm}>
                  {savingPerm ? <span className="spinner-border spinner-border-sm me-2"/> : <i className="bi bi-check-circle me-2"/>}
                  Salvar Permissões
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}